import React, { useCallback, useEffect, useState } from 'react';
import { useClient } from 'shared/apollo';
import { useUserActions } from 'shared/auth/hooks/useUserActions';
import { ReportVisibilityOptionsRender } from 'shared/components/WelcomeBack/ReportVisibilityModal';
import { Report } from 'shared/components/WelcomeBack/utils';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactModal, {
  modalFooterRadiusStyles,
  modalHeightAutoStyles,
  modalRadiusStyles,
  modelExtraRadiusStyles,
} from 'shared/design-system/components/TrueImpactModal';
import styled from 'styled-components';
import { SharingType } from './ReportSharingOptions';
import { updateReportVisibility } from './api';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { useIndicator } from 'shared/providers/IndicatorProvider';
import { hasDonorName } from 'shared/utils/donorNameMatch';
import { useFeatureFlags } from 'shared/providers/FeatureFlags/useFeatureFlags';

const customText =
  'True Impact gives you the option to make your impact reports publicly accessible through the True Impact reporting network. ' +
    'Before submitting your report, please select if you want to share this report publicly or if it is intended only for one specific ' +
    'donor.';
const restrictedText =
  'This report is defaulted to restricted. By choosing this option, all of your reports won’t be shared beyond your current donor. ' +
    'That means fewer opportunities for additional donors to discover your impact. To make changes, please select the report ' +
    'visibility to make it public.';

interface ReportBuilderSharingModelProps {
  report: Report;
}

const hasSponsorNameInAnswers = (
  answers: { text: string }[],
  sponsorName: string | null | undefined,
): boolean => answers.some(answer => hasDonorName(answer.text, sponsorName));

const StyledDiv = styled.div`
  max-width: 800px;
  padding: ${SPACERS.large};
  .sub-radio-option {
    background-color: ${COLORS.whiteBase};
  }
  .report-visibility-text {
    margin-bottom: ${SPACERS.small};
  }
  .visibility-note-text {
    margin-bottom: ${SPACERS.tiny};
  }
`;

const ReportBuilderSharingModel: React.FC<ReportBuilderSharingModelProps> = ({
  report,
}) => {
  const client = useClient();
  const { allAnswers } = useIndicator();
  const { visibility } = useUserActions();
  const levelOneFeedbackEnabled = useFeatureFlags('levelOneFeedback');
  const containsSponsorName =
    levelOneFeedbackEnabled &&
    hasSponsorNameInAnswers(allAnswers.answers, report.sponsorName);
  const [show, setShow] = useState<boolean>(
    report.visibility === 'RESTRICTED' && !containsSponsorName,
  );
  const [isCustom] = useState<boolean>(show ? visibility === 'CUSTOM' : false);
  const [value, setValue] = useState<SharingType>(
    containsSponsorName
      ? 'private'
      : report.visibility === 'PUBLIC'
      ? 'public'
      : 'private',
  );

  // If the report mentions the donor name, it must not remain public.
  // Auto-restrict any currently-public report so the DB matches the enforced UI.
  useEffect(() => {
    if (
      !report.cnOptedIn &&
      containsSponsorName &&
      report.visibility === 'PUBLIC' &&
      client
    ) {
      updateReportVisibility(client, {
        reportId: report.dbId,
        visibility: 'RESTRICTED',
      });
    }
  }, [
    client,
    containsSponsorName,
    report.cnOptedIn,
    report.dbId,
    report.visibility,
  ]);

  const handleClose = useCallback(() => {
    setShow(false);
  }, [setShow]);

  const onClose = useCallback(
    (value: SharingType) => {
      setValue(value);
      handleClose();
    },
    [setValue, handleClose],
  );

  if (report.cnOptedIn || containsSponsorName || report.level1DonorName) {
    return null;
  }

  return (
    <TrueImpactModal
      open={show}
      maxWidth="800px"
      hideHeader={true}
      next={{
        text: 'Done',
        onClick: handleClose,
      }}
      addStyles={
        modalHeightAutoStyles +
        modelExtraRadiusStyles +
        modalRadiusStyles +
        modalFooterRadiusStyles
      }
      onClose={handleClose}
    >
      <StyledDiv>
        <TrueImpactText className="report-visibility-text">
          {isCustom ? customText : restrictedText}
        </TrueImpactText>
        <ReportVisibilityOptionsRender
          value={value}
          report={report}
          onClose={onClose}
        />
      </StyledDiv>
    </TrueImpactModal>
  );
};

export default ReportBuilderSharingModel;
